
import { useState, useEffect, useCallback } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import type { DBProviderProps } from '../../types/app';
import { app } from '../../lib/firebase';
import { useApp } from '../../context/AppContext';
import { DBContext } from '../../context/DBContext';

/**
 * DBProvider
 *
 * Initializes a Firestore instance and exposes a small set of helpers via
 * context for basic reads/writes used by the demo UI. It also demonstrates
 * a simple first-run document creation for the logged-in user.
 */

export const DBProvider = ({ children }: DBProviderProps) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const { user, loading: userLoading } = useApp();
  const db = getFirestore(app);

  // On auth change, load or create a minimal user document and set a friendly message.
  useEffect(() => {
    (async () => {
      if (user && !userLoading && db) {
        setLoading(true);
        const ref = doc(db, 'users', user.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          // Create user document with Name and Role fields
          await setDoc(ref, { 
            Name: user.displayName || '', 
            Role: 'user'  // Default role is 'user'
          });
          setMessage(`Created document for ${user.uid} in Firestore!`);
        } else {
          setMessage(`Hello from ${user.uid}` );
        }
        setLoading(false);
      }
    })();
  }, [db, user, userLoading]);

  // Write helper: set a document at an arbitrary path. Used by the dashboard's
  // quick action button. Keeps Firestore access localized to provider.
  const setDocument = useCallback(async (path: string[], data: Record<string, unknown>) => {
    if (!db) return;
    const ref = doc(db, path[0], ...path.slice(1));
    await setDoc(ref, data);
  }, [db]);

  return (
    <DBContext.Provider value={{ db, message, loading, setMessage, setLoading, setDocument }}>
      {children}
    </DBContext.Provider>
  );
};
